/**
 * F-014 — Launch cooldown (BR-003).
 *
 * Tracks when the next launch becomes legal. Pure value type, accepts a
 * clock so tests can step time without sleeping.
 */

const DEFAULT_COOLDOWN_MS = 700;

export interface Cooldown {
  /** Returns true and arms the cooldown, false if still cooling. */
  tryArm(now?: number): boolean;
  remainingMs(now?: number): number;
  isReady(now?: number): boolean;
  /** Reset (used by F-019 restart). */
  reset(): void;
}

export interface CooldownOptions {
  durationMs?: number;
  now?: () => number;
}

export function createCooldown(options: CooldownOptions = {}): Cooldown {
  const duration = options.durationMs ?? DEFAULT_COOLDOWN_MS;
  const now = options.now ?? (() => performance.now());
  let nextReadyAt = 0;

  const remainingMs = (override?: number): number => {
    const t = override ?? now();
    return Math.max(0, nextReadyAt - t);
  };

  const isReady = (override?: number): boolean => remainingMs(override) === 0;

  const tryArm = (override?: number): boolean => {
    const t = override ?? now();
    if (t < nextReadyAt) return false;
    nextReadyAt = t + duration;
    return true;
  };

  const reset = (): void => {
    nextReadyAt = 0;
  };

  return { tryArm, remainingMs, isReady, reset };
}

export const __testing = { DEFAULT_COOLDOWN_MS };
